import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  buildManagedQPString,
  mergeQPIntoUrl,
  parseAndStrip
} from '@signal/core/dashboard/queryParamManager/utils'
import isStorybook from '@signal/utils/env/isStorybook'
import isTest from '@signal/utils/env/isTest'
import isUndefined from 'lodash/isUndefined'
import { setCookie } from '@signal/utils/cookies'
import { getCookie } from '@signal/utils/getCookie'
import { carryParameters } from '@signal/core/ab_tests'
import { MenuServiceItemType, MenuServiceSubItemType } from './types'

// uncomment this functions if testing locally
/* import isDevelopment from '@signal/utils/env/isDevelopment'

function mergeQPInDevEnvironment(link, mergedSearch) {
  let locationLink
  if (link.includes('clients-qa')) {
    locationLink = link
      .toString()
      .replace('clients-qa.purecars.com', 'dev.local.purecars.com:3079')
      .replace('/location/pma', '')
  }
  if (link.includes('apps-qa.purecars.com')) {
    locationLink = link
      .toString()
      .replace('apps-qa.purecars.com', 'dev.local.purecars.com:3000')
  }
  return mergeQPIntoUrl(`${locationLink || link}`, mergedSearch)
} */

const MENU_QP_STORAGE_KEY = 'menu-query-params'

const MENU_QP_LIVE_DELIMITER = 'live'
const MENU_QP_FINAL_DELIMITER = 'final'

// Type definitions for the hook parameters and return values
type QueryParams = Record<string, string | undefined>

interface StoredQueryParams {
  live: string
  final: string
}

type UseMenuLinksReturn = [string[], string[]]

const getBetween = (text: string | null, delimiter: string): string | null => {
  const regex = new RegExp(`${delimiter}(.*?)${delimiter}`)
  // TODO: REMOVE, FIND OUT WHAT IS CAUSING THIS BUG, text is null
  if (!text) return null
  const match = text.match(regex)
  return match ? match[1] : null
}

const parseEncodedQPs = (encodedQP: string): Record<string, string> => {
  const liveQP = getBetween(encodedQP, MENU_QP_LIVE_DELIMITER)
  const finalQP = getBetween(encodedQP, MENU_QP_FINAL_DELIMITER)

  return {
    [MENU_QP_LIVE_DELIMITER]: atob(liveQP || ''),
    [MENU_QP_FINAL_DELIMITER]: atob(finalQP || '')
  }
}

function getQPFromCookie(key: string): StoredQueryParams {
  try {
    const qpString = getCookie(key)
    if (!qpString) {
      return { live: '', final: '' }
    }
    const { live, final } = parseEncodedQPs(qpString)
    const ret: StoredQueryParams = {
      live,
      final
    }
    return ret
  } catch (error) {
    console.log(error)
    return { live: '', final: '' }
  }
}

/**
 * This hook manages query parameter persistence across menu navigation by storing them in cookies
 * and applying them to menu links. It ensures that supported query parameters are maintained
 * when navigating between different menu items and applications.
 *
 * @param submenu - Array of submenu items containing navigation links
 * @param menu - Array of main menu items containing navigation links
 * @param appSupportedQP - Array of query parameter names supported by the current application
 * @returns A tuple containing [subMenuLinks, navMenuLinks] with query parameters applied
 */
export function useMenuLinks(
  submenu: MenuServiceSubItemType[],
  menu: MenuServiceItemType[],
  appSupportedQP: string[]
): UseMenuLinksReturn {
  const links: string[] = submenu.map(obj => obj.link)
  const menuLinks: string[] = menu.map(
    item => item.link || (item.subMenuItems?.[0]?.link ?? '#')
  )

  // get live QP from react-router
  const location = useLocation()
  const search: string = location.search
  const liveQPs: QueryParams = parseAndStrip(search) as QueryParams

  // get stored QP from session storage
  const storage: StoredQueryParams = getQPFromCookie(MENU_QP_STORAGE_KEY)
  const { final: storedFinal, live: oldSearch } = storage

  const oldQPs: QueryParams = parseAndStrip(oldSearch || '') as QueryParams
  const storedQPs: QueryParams = parseAndStrip(storedFinal || '') as QueryParams

  // remove any stored QP that have been removed from the live QP, if the QP is supported in the current app
  // (don't remove QP unknown to the current app)
  Object.keys(oldQPs).forEach(qp => {
    if (appSupportedQP.includes(qp) && isUndefined(liveQPs[qp])) {
      delete storedQPs[qp]
    }
    // ensures we don't store non-appSupportedQPs

    // this is actually problematic because this removed all qps the app doesn't support.
    // which means when you go to a different app, the qps are removed unless the app supports them
    // and have a appSupportedQP array that is the same as the other app you navigated from
    //  making this feature/hook a bit useless

    if (!appSupportedQP.includes(qp)) {
      delete liveQPs[qp]
      delete storedQPs[qp]
    }
  })

  const mergedQPs: QueryParams = { ...storedQPs, ...liveQPs } // don't merge filters

  // creating our new search string now that we have remove the takeover QP if present
  const filteredSearch: string = buildManagedQPString(liveQPs)
  const mergedSearch: string = mergeQPIntoUrl(
    filteredSearch,
    buildManagedQPString(mergedQPs)
  )

  useEffect(() => {
    if (isTest() || isStorybook()) {
      return
    }

    // encode the binary strings to btoa strings
    const live = btoa(search)
    const final = btoa(mergedSearch.substring(1))

    // construct the final encoded QP string
    const encodedQP = [
      MENU_QP_LIVE_DELIMITER,
      live,
      MENU_QP_LIVE_DELIMITER,
      MENU_QP_FINAL_DELIMITER,
      final,
      MENU_QP_FINAL_DELIMITER
    ].join('')

    const clearCookie = (): void => {
      setCookie({
        name: MENU_QP_STORAGE_KEY,
        value: '',
        ttl: 0,
        domain: 'purecars.com',
        decode: false
      })
    }

    const setEncodedCookie = (value: string): void => {
      setCookie({
        name: MENU_QP_STORAGE_KEY,
        value,
        ttl: 0,
        domain: 'purecars.com',
        decode: false
      })
    }

    // check if the cookie size exceeds 4096 bytes
    if (encodedQP.length >= 4096) {
      clearCookie()
      return
    }
    // set the cookie if size is within limits
    appSupportedQP.length > 0 && setEncodedCookie(encodedQP)
  }, [appSupportedQP.length, mergedSearch, search])

  /*const linksWithFilters = createMenuOrSubMenuLinks(links, mergedSearch)
  const menuLinksWithFilters = createMenuOrSubMenuLinks(menuLinks, mergedSearch)

  // Removed the following lines as they were causing issues with the links
  // carrying over parameters for table state which caused unexpected behavior
  const subMenuLinks = useMemo(() => {
    if (isStorybook() || isTest()) {
      return links
    }
    return linksWithFilters
  }, [links, linksWithFilters])

  const navMenuLinks = useMemo(() => {
    if (isStorybook() || isTest()) {
      return menuLinks
    }
    return menuLinksWithFilters
  }, [menuLinks, menuLinksWithFilters])*/

  // comment the conditional below to remove AB-test & test locally
  if (carryParameters()) {
    // When carryParameters is enabled, return the original links without QP processing
    return [links, menuLinks]
  }

  return [links, menuLinks]
}

/*function createMenuOrSubMenuLinks(links, mergedSearch) {
  return links.map(link => {
    if (isTest() || isStorybook()) {
      return link
    }
    if (link === '#') {
      return link
    }
    //uncomment for local debugging
    // if (isDevelopment()) {
    //   return mergeQPInDevEnvironment(link, mergedSearch)
    // }
    return mergeQPIntoUrl(link, mergedSearch)
  })
}*/
